import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ApprovalRow {
  readonly aiApprovalId: string;
  readonly approvalCode: string;
  readonly approvalType: string;
  readonly title: string | null;
  readonly requestedBy: string;
  readonly decision: string;
  readonly approverId: string | null;
  readonly dueAt: string | null;
  readonly status: string;
}

const APPROVAL_TYPES = [
  'CONTENT_PUBLICATION', 'FINANCIAL_RECOMMENDATION', 'CONTRACT_CHANGE', 'VENDOR_DECISION',
  'COMPLIANCE_DECISION', 'POLICY_CHANGE', 'BUDGET_CHANGE', 'EXECUTIVE_COMMUNICATION',
];
const STATUSES = ['OPEN', 'CLOSED', 'CANCELLED'];
const DECISIONS = ['APPROVED', 'REJECTED', 'ESCALATED'];
const PAGE = 25;

/**
 * M38 — AI Approval Console (§19). The human-in-the-loop queue for critical AI
 * outputs (T510, BR-02/BR-05) — content publication, financial recommendations,
 * contract / vendor / compliance / policy / budget decisions and executive
 * communications. An authorised approver (APPROVAL_DECIDE), kept distinct from
 * the requester by SoD, approves, rejects or escalates each item; the decision
 * mirrors back onto the linked AI response. Filterable by type, decision and
 * status. Tenant isolation is enforced server-side (LAW 4). A 403 surfaces a
 * clear permission message.
 */
export function AiApprovalConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [approvalType, setApprovalType] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [selected, setSelected] = useState<ApprovalRow | null>(null);
  const [decision, setDecision] = useState('APPROVED');
  const [note, setNote] = useState('');
  const [escalatedTo, setEscalatedTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [decided, setDecided] = useState<ApprovalRow | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (approvalType) params.set('approvalType', approvalType);
    if (status) params.set('status', status);
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: ApprovalRow[]; total: number }>(`/v1/ai/approvals?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, approvalType, status, page, reload]);

  const openDecision = (a: ApprovalRow): void => {
    setSelected(a);
    setDecision('APPROVED');
    setNote('');
    setEscalatedTo('');
    setActionError(null);
    setDecided(null);
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selected) return;
    setActionError(null);
    setDecided(null);
    if (decision === 'ESCALATED' && !escalatedTo.trim()) { setActionError(t('m38.approval.escalationRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ApprovalRow }>(`/v1/ai/approvals/${selected.aiApprovalId}/decide`, {
        method: 'POST',
        body: {
          decision,
          decisionNote: note.trim() || undefined,
          escalatedTo: decision === 'ESCALATED' ? escalatedTo.trim() : undefined,
        },
        token,
      });
      setDecided(r.data);
      setSelected(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m38.approval.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="ai-approval-console">
      <h1>{t('m38.approval.title')}</h1>

      <section aria-label="approval-filters">
        <label>{t('m38.common.type')}
          <select aria-label="filter-type" value={approvalType} onChange={(e) => { setApprovalType(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allTypes')}</option>
            {APPROVAL_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m38.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {decided && <p aria-label="approval-decided">{t('m38.approval.decided', { code: decided.approvalCode, decision: decided.decision, status: decided.status })}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="approval-total">{t('m38.approval.total', { count: total })}</p>
          <DataTable
            ariaLabel="approval-table"
            rows={rows}
            rowKey={(a) => a.aiApprovalId}
            emptyMessage={t('m38.approval.noneMatch')}
            columns={[
              { key: 'approvalCode', header: t('m38.common.code'), render: (a) => a.approvalCode },
              { key: 'approvalType', header: t('m38.common.type'), render: (a) => a.approvalType },
              { key: 'title', header: t('m38.common.title'), render: (a) => a.title ?? '—' },
              { key: 'decision', header: t('m38.approval.decision'), render: (a) => a.decision },
              { key: 'status', header: t('m38.common.status'), render: (a) => a.status },
              { key: 'dueAt', header: t('m38.approval.colDue'), render: (a) => (a.dueAt ? at(a.dueAt) : '—') },
              { key: 'action', header: t('m38.common.action'), render: (a) => (
                    a.status === 'OPEN'
                      ? <Button variant="secondary" type="button" aria-label={`decide-${a.approvalCode}`} onClick={() => openDecision(a)}>{t('m38.approval.decide')}</Button>
                      : <span>—</span>
              ) },
            ]}
          />

          <nav aria-label="approval-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m38.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m38.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m38.common.next')}</Button>
          </nav>
        </>
      )}

      {selected && (
        <section aria-label="decide-approval">
          <h2>{t('m38.approval.decideHeading', { code: selected.approvalCode })}</h2>
          <form aria-label="decision-form" onSubmit={(e) => void submit(e)}>
            <label>{t('m38.approval.decision')}
              <select aria-label="field-decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
                {DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label>{t('m38.approval.note')}<input aria-label="field-note" value={note} onChange={(e) => setNote(e.target.value)} /></label>
            {decision === 'ESCALATED' && (
              <label>{t('m38.approval.escalateTo')}<input aria-label="field-escalated-to" value={escalatedTo} onChange={(e) => setEscalatedTo(e.target.value)} placeholder={t('m38.approval.approverIdPh')} /></label>
            )}
            <Button variant="primary" type="submit" aria-label="submit-decision" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.approval.submitDecision')}</Button>
            <Button variant="danger" type="button" aria-label="cancel-decision" onClick={() => setSelected(null)}>{t('common.cancel')}</Button>
          </form>
          {actionError && <p role="alert" aria-label="decision-error">{actionError}</p>}
        </section>
      )}
    </main>
  );
}
